/**
 * optional-products.js
 *
 * - Adds main product + selected optional stand-alone products to cart in one request
 * - Triggers cart drawer refresh/open
 * - Handles optional description "read more" only when text overflows 1 line
 * - Handles "view all / view less" for extra optional items
 */

(() => {
  let busy = false;

  const SELECTOR_FORMS = 'form[action*="/cart/add"]';
  const SELECTOR_OPTIONAL_CHECKED = '.js-optional:checked';
  const OPTIONAL_ROOT_SELECTOR = '.product-optionals';
  const OPTIONAL_DESC_SELECTOR = '.product-optionals__desc';
  const OPTIONAL_DESC_TEXT_SELECTOR = '.product-optionals__desc-text';
  const OPTIONAL_DESC_TOGGLE_SELECTOR = '.product-optionals__desc-toggle';
  const OPTIONAL_TOGGLE_SELECTOR = '.js-optionals-toggle, .product-optionals__toggle';
  const OPTIONAL_EXTRA_SELECTOR = '[data-optional-extra="1"]';

  let descRaf = null;
  let resizeTimer = null;
  let lastTouchTs = 0;

  function getCheckedOptionalInputs() {
    return Array.from(document.querySelectorAll(SELECTOR_OPTIONAL_CHECKED))
      .filter((el) => {
        const id = Number(el.value);
        return Number.isFinite(id) && id > 0;
      });
  }

  function getMainVariantIdFromForm(form) {
    const idInput = form.querySelector('input[name="id"]');
    const id = Number(idInput?.value);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  function getQtyFromForm(form) {
    const qtyInput = form.querySelector('input[name="quantity"]');
    const q = Number(qtyInput?.value || 1);
    return Number.isFinite(q) && q > 0 ? q : 1;
  }

  function numberToken(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return '';
    return String(Math.round(n * 10) / 10).replace('.', '_');
  }

  function getMainLineProperties(form) {
    const properties = {};
    const fd = new FormData(form);

    for (const [key, value] of fd.entries()) {
      const match = /^properties\[(.+)\]$/.exec(key);
      if (!match) continue;

      const propertyKey = match[1];
      const propertyValue = String(value ?? '');

      if (propertyValue !== '') {
        properties[propertyKey] = propertyValue;
      }
    }

    return properties;
  }

  function inferOptionalType(input) {
    const explicit = String(
      input?.dataset?.rpdType ||
      input?.dataset?.addonType ||
      ''
    ).toLowerCase();

    if (explicit === 'pods150') return 'coffee';
    if (explicit === 'accessory' || explicit === 'barmat') return 'accessory';

    const item = input?.closest('.product-optionals__item,label') || input?.parentElement;
    const text = [
      input?.dataset?.title,
      input?.dataset?.productTitle,
      input?.dataset?.sku,
      input?.dataset?.productSku,
      item?.textContent,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    // Accessori che possono contenere parole ambigue come "ciald"
    // (es. "portacialda") vanno classificati PRIMA del controllo caffè.
    if (
      text.includes('portacialda') ||
      text.includes('porta cialda') ||
      text.includes('porta-cialda') ||
      text.includes('pod holder') ||
      text.includes('podholder') ||
      text.includes('beccuccio') ||
      text.includes('barmat') ||
      text.includes('bar mat') ||
      text.includes('bottiglia') ||
      text.includes('vetro') ||
      text.includes('cavo') ||
      text.includes('svizzera') ||
      text.includes('swiss')
    ) {
      return 'accessory';
    }

    if (
      text.includes('ciald') ||
      text.includes('caff') ||
      text.includes('coffee') ||
      text.includes('150 pz') ||
      text.includes('150pz')
    ) {
      return 'coffee';
    }

    return 'accessory';
  }

  function getOptionalDiscountMeta(input, bundleState) {
    const inputId = String(input?.value || '');

    const stateAddon = Array.isArray(bundleState?.addons)
      ? bundleState.addons.find((addon) => String(addon?.id || '') === inputId)
      : null;

    if (stateAddon) {
      const type =
        stateAddon.type === 'pods150'
          ? 'coffee'
          : 'accessory';

      const discount = type === 'coffee'
        ? (bundleState?.hasUpgrade
          ? Number(stateAddon.bundle || 0)
          : Number(stateAddon.catalog || 0))
        : 0;

      return {
        type,
        discount: Number.isFinite(discount) ? discount : 0,
      };
    }

    // Defensive fallback only if the RPD state is unavailable.
    const type = inferOptionalType(input);

    const unlocked = !!(
      document.querySelector(
        '[data-role="upgrade-options"] input[type="checkbox"]:checked:not(:disabled), ' +
        '[data-role="upgrade-options"] input[type="radio"]:checked:not(:disabled)'
      )
    );

    const catalog = Number(
      input?.dataset?.rpdCatalogDiscount ||
      input?.dataset?.catalogDiscount ||
      (type === 'coffee' ? 15 : 0)
    );

    const bundle = Number(
      input?.dataset?.rpdBundleDiscount ||
      input?.dataset?.bundleDiscount ||
      (type === 'coffee' ? 20 : 0)
    );

    const value = unlocked ? bundle : catalog;

    return {
      type,
      discount: Number.isFinite(value) ? value : 0,
    };
  }

  function buildBundleItems(form, optionalInputs, bundleState) {
    const mainVariantId = getMainVariantIdFromForm(form);
    if (!mainVariantId) return [];

    const mainProperties = getMainLineProperties(form);

    // Use ONLY the value calculated by rpd-bundle-v6.js for the current
    // machine / upgrade / coffee / accessory combination.
    const machineDiscount = Number(bundleState?.machineDiscount || 0);
    const machineToken = numberToken(machineDiscount);

    if (machineToken) {
      mainProperties._faber_discount = `machine_${machineToken}`;
    } else {
      delete mainProperties._faber_discount;
    }

    // Explicit source of truth for cart cross-sell 15% / 20%.
    // Do not infer upgrade state from _gv_upgrade_labels.
    mainProperties._faber_has_upgrade = bundleState?.hasUpgrade ? '1' : '0';

    const items = [
      {
        id: mainVariantId,
        quantity: getQtyFromForm(form),
        ...(Object.keys(mainProperties).length ? { properties: mainProperties } : {}),
      },
    ];

    for (const input of optionalInputs) {
      const id = Number(input.value);
      if (!Number.isFinite(id) || id <= 0) continue;

      const meta = getOptionalDiscountMeta(input, bundleState);
      const token = numberToken(meta.discount);

      const properties = {};

      if (token) {
        const productType = String(input?.dataset?.productType || '').trim().toLowerCase();

        // SOLO Shopify Product type "Coffee Pods" può avere _faber_discount.
        if (meta.type === 'coffee' && productType === 'coffee pods') {
          properties._faber_discount = `coffee_${token}`;
        }
      }

      items.push({
        id,
        quantity: 1,
        ...(Object.keys(properties).length ? { properties } : {}),
      });
    }

    return items;
  }

  function getDrawerSectionIds() {
    const drawer = document.querySelector('cart-drawer-component');

    const raw =
      drawer?.getAttribute('sections') ||
      drawer?.dataset?.sections ||
      '';

    if (raw) {
      return raw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    }

    return ['cart-drawer', 'cart-icon-bubble'];
  }

  async function cartAdd(items) {
    const sections = getDrawerSectionIds();

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items,
        sections,
        sections_url: window.location.pathname,
      }),
    });

    const json = await res.json().catch(() => ({}));

    if (!res.ok) {
      console.error('cart/add.js failed', res.status, json);
      throw new Error(`cart_add_failed_${res.status}`);
    }

    return json;
  }

  async function getFreshCartAndSections() {
    const sections = getDrawerSectionIds();

    const cartPromise = fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    }).then((res) => {
      if (!res.ok) throw new Error(`cart_fetch_failed_${res.status}`);
      return res.json();
    });

    const sectionPromise = sections.length
      ? fetch(`/cart?sections=${encodeURIComponent(sections.join(','))}`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        }).then((res) => {
          if (!res.ok) throw new Error(`cart_sections_failed_${res.status}`);
          return res.json();
        })
      : Promise.resolve({});

    const [cart, renderedSections] = await Promise.all([
      cartPromise,
      sectionPromise,
    ]);

    return {
      ...cart,
      sections: renderedSections,
    };
  }

  function dispatchCartUpdate(cartResponse, sourceId = 'optional-products') {
    const ev = new Event('cart:update', { bubbles: true });

    ev.detail = {
      resource: cartResponse,
      sourceId,
      data: {
        source: 'optional-products',
        didError: false,
        itemCount: cartResponse?.item_count,
        sections: cartResponse?.sections,
      },
    };

    document.dispatchEvent(ev);
  }

  function openCartDrawer() {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }

    document.dispatchEvent(new Event('cart:open', { bubbles: true }));
  }

  function isElementVisible(el) {
    if (!el) return false;
    if (el.hidden) return false;
    if (el.closest('[hidden]')) return false;
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  function getLineHeightPx(el) {
    if (!el) return 0;
    const style = window.getComputedStyle(el);
    const raw = style.lineHeight;

    if (raw && raw.endsWith('px')) {
      const val = parseFloat(raw);
      if (Number.isFinite(val)) return val;
    }

    const fontSize = parseFloat(style.fontSize || '16');
    if (Number.isFinite(fontSize)) return fontSize * 1.2;

    return 19.2;
  }

  function resetOptionalDesc(desc, toggle) {
    if (!desc) return;

    desc.classList.remove('has-more', 'is-expanded');

    if (toggle) {
      toggle.style.display = 'none';
      toggle.setAttribute('aria-expanded', 'false');
      toggle.textContent = toggle.dataset.more || toggle.textContent || '';
    }
  }

  function updateOptionalDescription(desc) {
    if (!desc) return;

    const text = desc.querySelector(OPTIONAL_DESC_TEXT_SELECTOR);
    const toggle = desc.querySelector(OPTIONAL_DESC_TOGGLE_SELECTOR);

    if (!text || !toggle) return;

    resetOptionalDesc(desc, toggle);

    if (!isElementVisible(text)) return;

    const prevClamp = text.style.webkitLineClamp;
    const prevDisplay = text.style.display;
    const prevOverflow = text.style.overflow;

    const clampLines = Math.max(1, Number(desc.dataset.clampLines || 1));

    text.style.webkitLineClamp = String(clampLines);
    text.style.display = '-webkit-box';
    text.style.overflow = 'hidden';

    const lineHeight = getLineHeightPx(text);
    const maxAllowed = Math.ceil(lineHeight * clampLines + 1);
    const isOverflowing = text.scrollHeight > maxAllowed + 1;

    if (isOverflowing) {
      desc.classList.add('has-more');
      toggle.style.display = 'inline-block';
      toggle.setAttribute('aria-expanded', 'false');
      toggle.textContent = toggle.dataset.more || toggle.textContent || '';
    }

    text.style.webkitLineClamp = prevClamp;
    text.style.display = prevDisplay;
    text.style.overflow = prevOverflow;
  }

  function scheduleOptionalDescriptionsUpdate(root = document) {
    if (descRaf) cancelAnimationFrame(descRaf);

    descRaf = requestAnimationFrame(() => {
      root.querySelectorAll(OPTIONAL_DESC_SELECTOR).forEach(updateOptionalDescription);
      descRaf = null;
    });
  }

  async function handleOptionalAdd(form, submitBtn) {
    const optionalInputs = getCheckedOptionalInputs();
    if (!optionalInputs.length) return false;

    const mainVariantId = getMainVariantIdFromForm(form);
    if (!mainVariantId) return false;

    if (busy) return true;
    busy = true;

    if (submitBtn) submitBtn.disabled = true;

    try {
      // Recalculate the bundle at the exact moment of submit.
      // This prevents stale `hasUpgrade` / addon states from sending catalog 20%
      // when the PDP is actually showing a higher bundle percentage.
      let bundleState = window.__faberRpdBundleState || null;

      if (typeof window.__faberRpdBundleRefresh === 'function') {
        try {
          bundleState = await window.__faberRpdBundleRefresh();
        } catch (refreshError) {
          console.warn('RPD bundle refresh failed; using last state.', refreshError);
        }
      }

      const items = buildBundleItems(form, optionalInputs, bundleState);
      if (!items.length) return false;

      // Add first, then re-fetch the actual cart and rendered drawer sections.
      // The second request is important because Shopify Functions discounts can
      // be evaluated after the add response used by the old drawer flow.
      await cartAdd(items);

      const freshCart = await getFreshCartAndSections();
      dispatchCartUpdate(freshCart);
      document.dispatchEvent(
        new CustomEvent('rpd:bundle:added', {
          bubbles: true,
          detail: { cart: freshCart },
        })
      );

      requestAnimationFrame(() => {
        requestAnimationFrame(openCartDrawer);
      });

      return true;
    } catch (err) {
      console.error('Optional add failed:', err);
      alert('Errore durante l’aggiunta degli optional. Riprova.');
      return true;
    } finally {
      busy = false;
      if (submitBtn) submitBtn.disabled = false;
    }
  }

  function bindForm(form) {
    if (form.dataset.optionalsBound === '1') return;
    form.dataset.optionalsBound = '1';

    form.addEventListener('submit', async (e) => {
      const submitBtn = form.querySelector('[type="submit"]');
      const handled = await handleOptionalAdd(form, submitBtn);
      if (handled) e.preventDefault();
    });

    form.querySelectorAll('[type="submit"], button[name="add"]').forEach((btn) => {
      btn.addEventListener(
        'click',
        async (e) => {
          const handled = await handleOptionalAdd(form, btn);
          if (handled) {
            e.preventDefault();
            e.stopPropagation();
          }
        },
        true
      );
    });
  }

  function handleDescToggleClick(e) {
    const btn = e.target.closest?.(OPTIONAL_DESC_TOGGLE_SELECTOR);
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    const desc = btn.closest(OPTIONAL_DESC_SELECTOR);
    if (!desc || !desc.classList.contains('has-more')) return;

    const expanded = desc.classList.toggle('is-expanded');
    btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    btn.textContent = expanded
      ? (btn.dataset.less || 'Riduci')
      : (btn.dataset.more || 'Leggi di più');
  }

  function setOpen(wrap, open) {
    wrap.classList.toggle('is-open', open);

    const toggle = wrap.querySelector(OPTIONAL_TOGGLE_SELECTOR);
    if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');

    wrap.querySelectorAll(OPTIONAL_EXTRA_SELECTOR).forEach((el) => {
      el.hidden = !open;
    });

    setTimeout(() => scheduleOptionalDescriptionsUpdate(wrap), 40);
    setTimeout(() => scheduleOptionalDescriptionsUpdate(wrap), 160);
  }

  function bindOptionalRoot(wrap) {
    if (!wrap || wrap.dataset.optionalsToggleBound === '1') return;
    wrap.dataset.optionalsToggleBound = '1';

    const toggle = wrap.querySelector(OPTIONAL_TOGGLE_SELECTOR);
    if (!toggle) {
      scheduleOptionalDescriptionsUpdate(wrap);
      return;
    }

    const hasExtras = !!wrap.querySelector(OPTIONAL_EXTRA_SELECTOR);
    if (!hasExtras) {
      toggle.style.display = 'none';
    } else {
      setOpen(wrap, false);

      if (!toggle.matches('a,button,input,summary') && !toggle.hasAttribute('tabindex')) {
        toggle.setAttribute('tabindex', '0');
        toggle.setAttribute('role', toggle.getAttribute('role') || 'button');
      }
    }

    scheduleOptionalDescriptionsUpdate(wrap);
  }


  function normalizeAllCapsCardTitles(root = document) {
    root.querySelectorAll('.product-optionals__name, .upgrade-card .product-optionals__title').forEach((el) => {
      const raw = (el.textContent || '').trim();
      if (!raw) return;

      const letters = raw.replace(/[^\p{L}]+/gu, '');
      if (!letters || letters !== letters.toLocaleUpperCase()) return;

      const locale = document.documentElement.lang || undefined;
      const lower = raw.toLocaleLowerCase(locale);
      el.textContent = lower.replace(/\p{L}/u, (char) => char.toLocaleUpperCase(locale));
    });
  }

  function initOptionals(root = document) {
    normalizeAllCapsCardTitles(root);
    root.querySelectorAll(OPTIONAL_ROOT_SELECTOR).forEach(bindOptionalRoot);
    requestAnimationFrame(() => normalizeAllCapsCardTitles(root));
  }

  function handleOptionalToggleEvent(e) {
    const toggle = e.target.closest?.(OPTIONAL_TOGGLE_SELECTOR);
    if (!toggle) return;

    const wrap = toggle.closest?.(OPTIONAL_ROOT_SELECTOR);
    if (!wrap) return;

    const now = Date.now();
    if (e.type === 'click' && now - lastTouchTs < 650) {
      e.preventDefault();
      return;
    }
    if (e.type === 'touchend') lastTouchTs = now;

    e.preventDefault();
    e.stopPropagation();

    const open = wrap.classList.contains('is-open');
    setOpen(wrap, !open);
  }

  function init() {
    document.querySelectorAll(SELECTOR_FORMS).forEach(bindForm);
    initOptionals(document);
    scheduleOptionalDescriptionsUpdate(document);
  }

  document.addEventListener('DOMContentLoaded', init);

  document.addEventListener('shopify:section:load', (e) => {
    document.querySelectorAll(SELECTOR_FORMS).forEach(bindForm);
    initOptionals(e.target || document);
    scheduleOptionalDescriptionsUpdate(e.target || document);
  });

  document.addEventListener('variant:update', () => {
    document.querySelectorAll(SELECTOR_FORMS).forEach(bindForm);
    normalizeAllCapsCardTitles(document);
    scheduleOptionalDescriptionsUpdate(document);
  });

  document.addEventListener('click', handleDescToggleClick, true);
  document.addEventListener('touchend', handleOptionalToggleEvent, true);
  document.addEventListener('click', handleOptionalToggleEvent, true);

  document.addEventListener(
    'keydown',
    (e) => {
      const toggle = e.target.closest?.(OPTIONAL_TOGGLE_SELECTOR);
      if (!toggle) return;

      const wrap = toggle.closest?.(OPTIONAL_ROOT_SELECTOR);
      if (!wrap) return;

      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggle.click();
      }
    },
    true
  );

  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => scheduleOptionalDescriptionsUpdate(document), 120);
  });

  window.addEventListener('load', () => {
    scheduleOptionalDescriptionsUpdate(document);
    setTimeout(() => scheduleOptionalDescriptionsUpdate(document), 180);
    setTimeout(() => scheduleOptionalDescriptionsUpdate(document), 600);
  });
})();